import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Search, ArrowRight, ClipboardList, Layers, AlertTriangle } from "lucide-react";
import { api, formatApiError } from "../lib/api";
import { PageHeader, Panel } from "../lib/ui";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { toast } from "sonner";

const Crumb = () => <div className="text-[11px] uppercase tracking-wide text-wmuted mb-1">Sales / Pre-Order / Overview PO</div>;

export default function PreOrderOverview() {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [q, setQ] = useState("");

  const load = () => {
    setLoading(true); setError(false);
    api.get("/preorders/virtual-warehouse", { params: { batch_status: "Open" } })
      .then((r) => setData(r.data))
      .catch((e) => { setError(true); toast.error(formatApiError(e.response?.data?.detail)); })
      .finally(() => setLoading(false));
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const t = data?.totals || {};
  const products = useMemo(() => {
    const map = {};
    for (const it of data?.items || []) {
      const key = it.product_name || "-";
      const p = map[key] || { product_name: key, planned: 0, reserved: 0, sold: 0, remaining: 0, ready: 0, arrived: 0, variants: 0 };
      p.planned += it.planned || 0; p.reserved += it.reserved || 0; p.sold += it.sold || 0;
      p.remaining += it.remaining || 0; p.ready += it.ready || 0; p.arrived += it.arrived || 0; p.variants += 1;
      map[key] = p;
    }
    let arr = Object.values(map).sort((a, b) => b.reserved - a.reserved);
    if (q.trim()) arr = arr.filter((p) => p.product_name.toLowerCase().includes(q.toLowerCase()));
    return arr;
  }, [data, q]);

  return (
    <div className="space-y-4" data-testid="preorder-overview-page">
      <div><Crumb />
        <PageHeader title="Overview PO" subtitle="Ringkasan kuota Pre-Order dari perhitungan existing (batch Open). Angka mengikuti kuota per batch — tidak menyimpulkan asal marketplace.">
          <div className="flex gap-2">
            <Button variant="outline" onClick={() => navigate("/sales/preorder-warehouse")} className="border-cocoa text-cocoa" data-testid="po-ov-goto-orders"><ClipboardList className="w-4 h-4 mr-1.5" />Pesanan PO</Button>
            <Button variant="outline" onClick={() => navigate("/sales/preorder-batches")} className="border-cocoa text-cocoa" data-testid="po-ov-goto-batches"><Layers className="w-4 h-4 mr-1.5" />Campaign & Batch</Button>
          </div>
        </PageHeader>
      </div>

      {loading ? (
        <Panel className="p-10 text-center text-wmuted" data-testid="po-ov-loading">Memuat ringkasan…</Panel>
      ) : error ? (
        <Panel className="p-10 text-center" data-testid="po-ov-error"><div className="text-status-reject mb-2">Gagal memuat data.</div><Button variant="outline" onClick={load} className="border-cocoa text-cocoa">Coba lagi</Button></Panel>
      ) : (
        <>
          <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
            {[["Rencana", t.planned], ["Di-Keep", t.reserved], ["Terjual", t.sold], ["Barang Datang", t.arrived], ["Sisa Kuota", t.remaining]].map(([l, v]) => (
              <Panel key={l} className="p-3" data-testid={`po-ov-card-${l}`}><div className="text-xs text-wmuted">{l}</div><div className="text-2xl font-heading text-cocoa">{v || 0}</div></Panel>
            ))}
          </div>
          <Panel className="p-0 overflow-hidden">
            <div className="flex items-center gap-2 px-4 py-3 border-b border-taupe/15">
              <h3 className="font-semibold text-cocoa">Ringkasan per Produk</h3>
              <span className="text-xs text-wmuted">{data?.batch_count || 0} batch Open</span>
              <div className="relative ml-auto w-64"><Search className="w-4 h-4 absolute left-2 top-2.5 text-wmuted" /><Input className="pl-8 bg-white" placeholder="Cari produk…" value={q} onChange={(e) => setQ(e.target.value)} data-testid="po-ov-search" /></div>
            </div>
            <Table>
              <TableHeader><TableRow className="hover:bg-transparent"><TableHead>Produk</TableHead><TableHead className="text-center">Varian</TableHead><TableHead className="text-right">Rencana</TableHead><TableHead className="text-right">Di-Keep</TableHead><TableHead className="text-right">Terjual</TableHead><TableHead className="text-right">Barang Datang</TableHead><TableHead className="text-right">Sisa</TableHead><TableHead className="w-10"></TableHead></TableRow></TableHeader>
              <TableBody>
                {products.map((p) => (
                  <TableRow key={p.product_name} data-testid={`po-ov-row-${p.product_name}`}>
                    <TableCell className="font-medium text-cocoa">{p.product_name}</TableCell>
                    <TableCell className="text-center text-wmuted">{p.variants}</TableCell>
                    <TableCell className="text-right">{p.planned}</TableCell>
                    <TableCell className="text-right text-amber-600">{p.reserved}</TableCell>
                    <TableCell className="text-right">{p.sold}</TableCell>
                    <TableCell className="text-right text-emerald-600">{p.arrived}</TableCell>
                    <TableCell className="text-right font-semibold">{p.remaining}</TableCell>
                    <TableCell><button onClick={() => navigate(`/sales/preorder/analysis?q=${encodeURIComponent(p.product_name)}`)} title="Analisis varian" className="text-blue-600"><ArrowRight className="w-4 h-4" /></button></TableCell>
                  </TableRow>
                ))}
                {!products.length && <TableRow><TableCell colSpan={8} className="text-center text-wmuted py-10">Belum ada kuota PO pada batch Open.</TableCell></TableRow>}
              </TableBody>
            </Table>
          </Panel>
          <div className="flex items-start gap-2 text-xs text-wmuted bg-ivory rounded-lg p-3"><AlertTriangle className="w-4 h-4 text-amber-500 shrink-0 mt-0.5" />Angka di atas adalah kuota per batch (Planned/Reserved/Sold/Remaining) dari perhitungan existing. Reserved pada batch Online tidak otomatis berarti berasal dari marketplace.</div>
        </>
      )}
    </div>
  );
}
